/**
 * Chart.js helpers for Alpine.js integration.
 * Usage in Blade:
 *   <canvas x-data="barChart({ labels: [...], datasets: [...] })"></canvas>
 */
import {
    Chart,
    LineController, LineElement, PointElement,
    BarController, BarElement,
    DoughnutController, ArcElement,
    CategoryScale, LinearScale,
    Tooltip, Legend, Filler,
    Title,
} from 'chart.js';

Chart.register(
    LineController, LineElement, PointElement,
    BarController, BarElement,
    DoughnutController, ArcElement,
    CategoryScale, LinearScale,
    Tooltip, Legend, Filler,
    Title,
);

// ── Shared defaults ──────────────────────────────────────
const fontFamily = "'Inter', ui-sans-serif, sans-serif";

function isDark() {
    return document.documentElement.classList.contains('dark');
}

function colors() {
    return isDark()
        ? { text: '#94a3b8', grid: 'rgba(148,163,184,0.1)', surface: '#1e293b' }
        : { text: '#64748b', grid: 'rgba(100,116,139,0.1)', surface: '#ffffff' };
}

const PRIMARY  = { solid: '#4f46e5', alpha: 'rgba(79,70,229,0.15)' };
const SUCCESS  = { solid: '#10b981', alpha: 'rgba(16,185,129,0.15)' };
const WARNING  = { solid: '#f59e0b', alpha: 'rgba(245,158,11,0.15)' };
const DANGER   = { solid: '#ef4444', alpha: 'rgba(239,68,68,0.15)' };
const INFO     = { solid: '#0ea5e9', alpha: 'rgba(14,165,233,0.15)' };
const PALETTE  = [PRIMARY.solid, SUCCESS.solid, WARNING.solid, DANGER.solid, INFO.solid, '#8b5cf6', '#ec4899'];

function baseOptions(theme) {
    const c = colors();
    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: { color: c.text, font: { family: fontFamily, size: 12 }, boxWidth: 12, padding: 16 },
            },
            tooltip: {
                backgroundColor: theme === 'dark' ? '#1e293b' : '#fff',
                borderColor: c.grid,
                borderWidth: 1,
                titleColor: isDark() ? '#f1f5f9' : '#0f172a',
                bodyColor: c.text,
                padding: 10,
                cornerRadius: 8,
                titleFont: { family: fontFamily, weight: '600' },
                bodyFont:  { family: fontFamily },
            },
        },
        scales: {
            x: {
                ticks: { color: c.text, font: { family: fontFamily, size: 11 } },
                grid:  { color: c.grid },
                border: { dash: [4, 4] },
            },
            y: {
                ticks: { color: c.text, font: { family: fontFamily, size: 11 } },
                grid:  { color: c.grid },
                border: { dash: [4, 4] },
                beginAtZero: true,
            },
        },
    };
}

// ── Chart factories ──────────────────────────────────────
function lineChart(config = {}) {
    return {
        _chart: null,
        init() {
            const opts = baseOptions();
            opts.plugins.legend.display = config.showLegend ?? true;
            this._chart = new Chart(this.$el, {
                type: 'line',
                data: {
                    labels: config.labels ?? [],
                    datasets: (config.datasets ?? []).map((ds, i) => ({
                        tension: 0.4,
                        fill: ds.fill ?? false,
                        borderWidth: 2,
                        pointRadius: 3,
                        pointHoverRadius: 5,
                        borderColor: ds.color ?? PALETTE[i % PALETTE.length],
                        backgroundColor: ds.fill ? (ds.alpha ?? PRIMARY.alpha) : 'transparent',
                        ...ds,
                    })),
                },
                options: { ...opts, ...config.options },
            });
            // Reactively update when dark mode changes
            this.$watch('$store', () => this._refreshTheme());
        },
        _refreshTheme() {
            if (!this._chart) return;
            const c = colors();
            this._chart.options.scales.x.ticks.color = c.text;
            this._chart.options.scales.y.ticks.color = c.text;
            this._chart.update('none');
        },
        destroy() { this._chart?.destroy(); },
    };
}

function barChart(config = {}) {
    return {
        _chart: null,
        init() {
            const opts = baseOptions();
            opts.plugins.legend.display = config.showLegend ?? false;
            this._chart = new Chart(this.$el, {
                type: 'bar',
                data: {
                    labels: config.labels ?? [],
                    datasets: (config.datasets ?? []).map((ds, i) => ({
                        borderRadius: 6,
                        borderWidth: 0,
                        backgroundColor: ds.color ?? PALETTE[i % PALETTE.length],
                        ...ds,
                    })),
                },
                options: { ...opts, ...config.options },
            });
        },
        destroy() { this._chart?.destroy(); },
    };
}

function doughnutChart(config = {}) {
    return {
        _chart: null,
        init() {
            this._chart = new Chart(this.$el, {
                type: 'doughnut',
                data: {
                    labels: config.labels ?? [],
                    datasets: [{
                        data: config.data ?? [],
                        backgroundColor: config.colors ?? PALETTE,
                        borderWidth: 2,
                        borderColor: isDark() ? '#1e293b' : '#ffffff',
                        hoverBorderColor: isDark() ? '#334155' : '#f8fafc',
                        hoverOffset: 4,
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: config.cutout ?? '72%',
                    plugins: {
                        legend: {
                            position: config.legendPosition ?? 'bottom',
                            labels: {
                                color: colors().text,
                                font: { family: fontFamily, size: 12 },
                                boxWidth: 10,
                                padding: 14,
                                usePointStyle: true,
                            },
                        },
                        tooltip: {
                            backgroundColor: isDark() ? '#1e293b' : '#fff',
                            borderColor: colors().grid,
                            borderWidth: 1,
                            titleColor: isDark() ? '#f1f5f9' : '#0f172a',
                            bodyColor: colors().text,
                            padding: 10,
                            cornerRadius: 8,
                        },
                    },
                    ...config.options,
                },
            });
        },
        destroy() { this._chart?.destroy(); },
    };
}

// ── Expose globally for Alpine.js ─────────────────────────
window.lineChart     = lineChart;
window.barChart      = barChart;
window.doughnutChart = doughnutChart;
window.CHART_COLORS  = { PRIMARY, SUCCESS, WARNING, DANGER, INFO, PALETTE };
